import { useCallback, useState } from 'react';
import { CreditCard } from 'lucide-react';

import { Button } from '@monetr/interface/components/Button';
import { useAuthentication } from '@monetr/interface/hooks/useAuthentication';
import request from '@monetr/interface/util/request';
import { useSnackbar } from '@monetr/notify';

import styles from './SetupBillingButton.module.scss';

/**
 * The SetupBillingButton should only be used on the setup page, it is intended to be a way to manage your billing
 * settings if you do not have an active link for some reason.
 */
export default function SetupBillingButton(): React.JSX.Element | null {
  const { data } = useAuthentication();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const handleManageSubscription = useCallback(() => {
    setLoading(true);
    // If the customer has a subscription then we want to just manage it. This will allow a customer to fix a
    // subscription for a card that has failed payment or something similar.
    request<{ url: string }>({ method: 'GET', url: '/api/billing/portal' })
      .then(result => window.location.assign(result.data.url))
      .catch(error => {
        setLoading(false);
        enqueueSnackbar(error?.response?.data?.error || 'Failed to prepare Stripe billing session.', {
          variant: 'error',
          disableWindowBlurListener: true,
        });
      });
  }, [enqueueSnackbar]);

  if (!data?.hasSubscription) {
    return null;
  }

  return (
    <Button className={styles.button} disabled={loading} onClick={handleManageSubscription} variant='secondary'>
      <CreditCard className={styles.icon} />
      Manage Your Subscription
    </Button>
  );
}
